import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLBlockInline = () => {
  return (
    <div className="html-container">
      <h1>HTML – Block & Inline Elements</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML elements are categorized as <strong>block-level</strong> and <strong>inline</strong> elements. Understanding the difference is essential for layout and styling.
        </p>
      </section>

      {/* 2. Block-level Elements */}
      <section id="block-elements">
        <h2>2. Block-level Elements</h2>
        <p>Block elements occupy the full width available and start on a new line. They can contain other block or inline elements.</p>
        <ul>
          <li>Examples: <code>&lt;div&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;h1&gt;</code>–<code>&lt;h6&gt;</code>, <code>&lt;section&gt;</code>, <code>&lt;article&gt;</code></li>
        </ul>
        <p>Example HTML:</p>
        <pre>
          <code>{`<div>
  <h2>Heading</h2>
  <p>This is a paragraph inside a div.</p>
</div>`}</code>
        </pre>
      </section>

      {/* 3. Inline Elements */}
      <section id="inline-elements">
        <h2>3. Inline Elements</h2>
        <p>Inline elements do not start on a new line and only take up as much width as necessary. They are typically used for formatting text.</p>
        <ul>
          <li>Examples: <code>&lt;span&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;code&gt;</code></li>
        </ul>
        <p>Example HTML:</p>
        <pre>
          <code>{`<p>This is a <strong>bold</strong> word inside a paragraph.</p>
<p>Visit <a href="https://example.com">our website</a> for more info.</p>`}</code>
        </pre>
      </section>

      {/* 4. Differences Between Block & Inline */}
      <section id="differences">
        <h2>4. Differences Between Block & Inline</h2>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Block</th>
              <th>Inline</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Starts on a new line</td>
              <td>Yes</td>
              <td>No</td>
            </tr>
            <tr>
              <td>Width</td>
              <td>Full available width</td>
              <td>As wide as content</td>
            </tr>
            <tr>
              <td>Can contain</td>
              <td>Block & Inline</td>
              <td>Only Inline</td>
            </tr>
            <tr>
              <td>Examples</td>
              <td>&lt;div&gt;, &lt;p&gt;, &lt;h1&gt;</td>
              <td>&lt;span&gt;, &lt;a&gt;, &lt;strong&gt;</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 5. Inline-Block */}
      <section id="inline-block">
        <h2>5. Inline-Block Elements</h2>
        <p>
          <code>inline-block</code> allows elements to behave like inline elements while maintaining block properties such as width and height.
        </p>
        <pre>
          <code>{`<span style={{ display: "inline-block", width: "150px", height: "50px", background: "#007bff" }}>
  Inline-block box
</span>`}</code>
        </pre>
      </section>

      {/* 6. Best Practices */}
      <section id="best-practices">
        <h2>6. Best Practices</h2>
        <ul>
          <li>Use block elements for structural layout.</li>
          <li>Use inline elements for formatting text.</li>
          <li>Use CSS <code>display</code> property to change behavior if necessary.</li>
          <li>Avoid using block elements inside inline elements directly.</li>
        </ul>
      </section>

      {/* 7. Summary */}
      <section id="summary">
        <h2>7. Summary</h2>
        <ul>
          <li>Block elements start on a new line and occupy full width.</li>
          <li>Inline elements do not start on a new line and occupy minimal width.</li>
          <li>Inline-block combines features of both block and inline elements.</li>
          <li>Proper use improves layout control and semantic HTML structure.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLBlockInline;
